import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DataGrid, KpiCard, Spinner,
  type DataGridColumn,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  AlertTriangle, ChevronRight, FileText, IndianRupee, Plus, Printer, RefreshCw, Share2, Truck, TrendingUp, Upload,
} from 'lucide-react';
import { printDeliveryChallan, printInvoiceByFormat, type TenantHeader, type InvoicePrintInput, type HsnSummaryRow } from '../../print/templates';
import { getAssignedFormat } from '../../print/formats';
import { getTenantHeader, getPrintBrandingInlined, getTenantHeaderInlined, printQrUrl } from '../../print/branding';
import DineInOpenOrders from '../restaurant/DineInOpenOrders';

/**
 * Specialized list view for SalesInvoice — supersedes the generic
 * registry-driven grid by adding domain-specific KPIs (revenue,
 * outstanding, count-by-status) and a "+ New invoice" CTA that lands
 * directly in the multi-line composer at /panel/sales/invoices/new.
 */

interface InvoiceRow extends Record<string, unknown> {
  id?: number | string;
  invoiceId?: string;
  invoiceNumber?: string;
  invoiceDate?: string;
  dueDate?: string;
  customerId?: string;
  customerName?: string;
  customerAddress?: string;
  customerCity?: string;
  customerState?: string;
  customerPincode?: string;
  customerGstin?: string;
  customerPhone?: string;
  vehicleNumber?: string;
  driver?: string;
  totalAmount?: number;
  balanceAmount?: number;
  status?: string;
}

interface InvoiceLineRow {
  description?: string;
  productName?: string;
  hsnCode?: string;
  quantity?: number;
  qty?: number;
  unit?: string;
}

type Source = 'All' | 'Self' | 'POS' | 'Dine-in';

/**
 * Per-module page metadata. The invoice list is now SEPARATED by module —
 * Sales (manual/B2B), POS (retail), and Dine-in (restaurant) each get their
 * own route + page locked to that channel, instead of one combined grid.
 */
const MODULE_META: Record<Source, { title: string; subtitle: string; newLabel: string; newTo: string; canUpload: boolean }> = {
  All:       { title: 'All Invoices',   subtitle: 'Every channel · live /api/sales/SalesInvoice', newLabel: 'New invoice',  newTo: '/panel/sales/invoices/new', canUpload: true },
  Self:      { title: 'Sales Invoices',  subtitle: 'Manual & B2B invoices',                        newLabel: 'New invoice',  newTo: '/panel/sales/invoices/new', canUpload: true },
  POS:       { title: 'POS Invoices',    subtitle: 'Retail point-of-sale bills',                   newLabel: 'New POS sale', newTo: '/panel/pos/billing',        canUpload: false },
  'Dine-in': { title: 'Dine-in Bills',   subtitle: 'Restaurant table bills',                       newLabel: 'Open floor',   newTo: '/panel/restaurant/floor',   canUpload: false },
};

/** Cross-link tabs so a user can hop between the separated module views. */
const MODULE_TABS: Array<{ source: Exclude<Source, 'All'>; label: string; to: string }> = [
  { source: 'Self',    label: 'Sales',   to: '/panel/sales/invoices' },
  { source: 'POS',     label: 'POS',     to: '/panel/pos/invoices' },
  { source: 'Dine-in', label: 'Dine-in', to: '/panel/restaurant/invoices' },
];

export default function SalesInvoiceList({ source: lockedSource }: { source?: Exclude<Source, 'All'> } = {}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [tenant, setTenant] = useState<TenantHeader>({ name: 'KSS BillBook' });
  const [printingDc, setPrintingDc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // The module is fixed by the route (Sales / POS / Dine-in). Only when the
  // page is mounted with no prop does it show every channel ('All').
  const source: Source = lockedSource ?? 'All';
  const meta = MODULE_META[source];

  // Source channel: prefer the explicit `invoiceType` column; fall back
  // to the code prefix (POS-/DIN-) for rows saved before it was tagged.
  const sourceOf = (r: InvoiceRow): 'Self' | 'POS' | 'Dine-in' => {
    const t = String((r as { invoiceType?: unknown }).invoiceType ?? '').trim().toLowerCase();
    if (t === 'pos') return 'POS';
    if (t === 'dine-in' || t === 'dinein' || t === 'dine in') return 'Dine-in';
    if (t === 'self' || t === 'sales') return 'Self';
    const c = String((r as { code?: string }).code ?? r.invoiceNumber ?? '').toUpperCase();
    if (c.startsWith('POS')) return 'POS';
    if (c.startsWith('DIN')) return 'Dine-in';
    return 'Self';
  };

  const visibleRows = useMemo(
    () => {
      const base = source === 'All' ? rows : rows.filter((r) => sourceOf(r) === source);
      // Newest first: id is monotonic from the backend (invoiceDate is missing
      // on some POS/dine-in rows), with a date fallback.
      return [...base].sort((a, b) => {
        const idA = Number((a as { id?: unknown }).id) || 0;
        const idB = Number((b as { id?: unknown }).id) || 0;
        if (idA !== idB) return idB - idA;
        return Date.parse(String((b as { invoiceDate?: unknown }).invoiceDate ?? '')) -
               Date.parse(String((a as { invoiceDate?: unknown }).invoiceDate ?? ''));
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, source],
  );

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      // Tenant header for prints — sourced from admin-set branding (no
      // backend tenant endpoint; `/tenants/Tenant/me` → 404, gap G86).
      setTenant(getTenantHeader());
      const r = await apiClient.get('/sales/SalesInvoice');
      const d = r.data;
      let list: InvoiceRow[] = [];
      if (Array.isArray(d)) list = d as InvoiceRow[];
      else if (d && typeof d === 'object') {
        const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
        if (Array.isArray(obj.items)) list = obj.items as InvoiceRow[];
        else if (Array.isArray(obj.data)) list = obj.data as InvoiceRow[];
        else if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
          list = (obj.data as { items: unknown[] }).items as InvoiceRow[];
        }
      }
      setRows(list);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Re-fetch when the tab/window regains focus so an invoice saved in
    // another flow (composer, POS, restaurant bill) shows up without a
    // manual reload.
    const onFocus = () => void load();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // §35 schema rename (verified via inspect-invoice-schema.mjs):
  //   totalAmount    → grandTotal
  //   balanceAmount  → balance
  //   status (string Draft|Posted|...) → invoiceStatus + paymentStatus
  // Read both names so the KPIs work against the live API today and
  // any tenant still on the pre-§35 shape.
  const kpis = useMemo(() => {
    const totalOf = (r: InvoiceRow) =>
      Number(
        (r as { grandTotal?: unknown }).grandTotal
          ?? r.totalAmount
          ?? 0,
      );
    const balanceOf = (r: InvoiceRow) =>
      Number(
        (r as { balance?: unknown }).balance
          ?? (r as { balanceAmount?: unknown }).balanceAmount
          ?? totalOf(r),
      );
    const workflowStatusOf = (r: InvoiceRow): string => {
      const wf = (r as { invoiceStatus?: unknown; paymentStatus?: unknown });
      return String(wf.invoiceStatus ?? wf.paymentStatus ?? r.status ?? '').toLowerCase();
    };

    const total = visibleRows.length;
    const totalRevenue = visibleRows.reduce((s, r) => s + totalOf(r), 0);
    const outstanding = visibleRows
      .filter((r) => {
        const s = workflowStatusOf(r);
        return s !== 'paid' && s !== 'cancelled';
      })
      .reduce((s, r) => s + balanceOf(r), 0);
    const overdue = visibleRows.filter((r) => {
      if (!r.dueDate) return false;
      const due = Date.parse(String(r.dueDate));
      const isOverdue = !Number.isNaN(due) && due < Date.now();
      const s = workflowStatusOf(r);
      return isOverdue && s !== 'paid' && s !== 'cancelled';
    }).length;
    return { total, totalRevenue, outstanding, overdue };
  }, [visibleRows]);

  const columns = useMemo<DataGridColumn<InvoiceRow>[]>(
    () => {
      const cols: DataGridColumn<InvoiceRow>[] = [
      { accessor: 'invoiceNumber', title: 'Invoice #', sortable: true, filterable: true,
        render: (r) => (
          <Link
            to={`/panel/sales/invoices/${encodeURIComponent(String(r.invoiceId ?? r.id ?? ''))}`}
            className="font-medium text-primary hover:underline"
          >
            {String(r.invoiceNumber ?? r.id ?? '—')}
          </Link>
        )
      },
      { accessor: 'invoiceDate', title: 'Date', sortable: true,
        render: (r) => fmtDate(r.invoiceDate) },
      { accessor: 'customerName', title: 'Customer', sortable: true, filterable: true },
      { accessor: 'source' as never, title: 'Source', width: 90,
        render: (r) => <SourcePill label={sourceOf(r)} /> },
      { accessor: 'dueDate', title: 'Due', sortable: true,
        render: (r) => fmtDate(r.dueDate) },
      { accessor: 'grandTotal', title: 'Total', sortable: true,
        render: (r) => `₹ ${inr(Number((r as { grandTotal?: unknown }).grandTotal ?? r.totalAmount ?? 0))}` },
      { accessor: 'balance', title: 'Outstanding', sortable: true,
        render: (r) => `₹ ${inr(Number((r as { balance?: unknown }).balance ?? (r as { balanceAmount?: unknown }).balanceAmount ?? 0))}` },
      { accessor: 'status', title: 'Status', sortable: true, filterable: true,
        render: (r) => <StatusPill v={String(r.status ?? '')} /> },
      { accessor: '__actions' as never, title: '', width: 90,
        render: (r) => {
          const id = String(r.invoiceId ?? r.id ?? '');
          const busy = printingDc === id;
          return (
            <div className="flex items-center justify-end gap-1.5">
              <button
                type="button"
                onClick={() => void handlePrintInvoice(r)}
                title="Print invoice"
                aria-label="Print invoice"
                className="text-content-muted hover:text-primary"
              >
                <Printer size={15} />
              </button>
              <button
                type="button"
                onClick={() => void handleShare(r)}
                title="Share invoice"
                aria-label="Share invoice"
                className="text-content-muted hover:text-primary"
              >
                <Share2 size={15} />
              </button>
              <button
                type="button"
                onClick={() => void handleDc(r)}
                disabled={busy}
                title="Print delivery challan"
                aria-label="Print delivery challan"
                className="text-content-muted hover:text-primary disabled:opacity-50"
              >
                {busy ? <Spinner /> : <Truck size={15} />}
              </button>
              <Link
                to={`/panel/sales/invoices/${encodeURIComponent(id)}`}
                className="text-content-muted hover:text-primary"
                title="Open invoice"
              >
                <ChevronRight size={16} />
              </Link>
            </div>
          );
        },
      },
      ];
      // When the page is locked to one module the Source column is redundant.
      return cols.filter((c) => source === 'All' || (c as { accessor?: unknown }).accessor !== 'source');
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [printingDc, tenant, source],
  );

  /** Fetch this invoice's line rows (the generic endpoint ignores ?invoiceId=).
   *  Falls back to the header `description` JSON for POS / dine-in invoices
   *  that have no separate line rows — otherwise the print shows no items. */
  async function fetchInvoiceLines(id: string, description?: unknown): Promise<InvoiceLineRow[]> {
    try {
      const ls = await apiClient.get('/sales/SalesInvoiceLine');
      const d = ls.data;
      const all = (Array.isArray(d) ? d : (d?.items ?? d?.data?.items ?? d?.data ?? [])) as InvoiceLineRow[];
      const mine = all.filter((l) => String((l as { salesInvoiceId?: unknown }).salesInvoiceId ?? '') === String(id));
      if (mine.length) return mine;
    } catch { /* fall through to description */ }
    // No line rows — recover from the header description envelope.
    try {
      const env = JSON.parse(String(description ?? '{}')) as { lines?: Array<Record<string, unknown>>; items?: Array<Record<string, unknown>> };
      const src = (Array.isArray(env.lines) && env.lines.length ? env.lines : (Array.isArray(env.items) ? env.items : []));
      return src.map((l) => {
        const qty = Number(l.quantity ?? l.qty ?? 1) || 1;
        const price = Number(l.unitPrice ?? l.rate ?? l.price ?? 0);
        const taxRate = Number(l.taxRate ?? 0);
        return {
          productName: String(l.name ?? l.productName ?? l.itemName ?? '—'),
          quantity: qty, unitPrice: price, taxRate,
          taxableAmount: Math.round(qty * price * 100) / 100,
          lineTotal: Math.round(qty * price * (1 + taxRate / 100) * 100) / 100,
        } as unknown as InvoiceLineRow;
      });
    } catch { return []; }
  }

  async function handlePrintInvoice(r: InvoiceRow) {
    const id = String(r.invoiceId ?? r.id ?? '');
    if (!id) return;
    const lines = await fetchInvoiceLines(id, (r as Record<string, unknown>).description);
    // Inline uploaded logo/signature/QR (auth-only URLs) as data URIs so the
    // print window can actually render them.
    const b = await getPrintBrandingInlined();
    const tenantH = await getTenantHeaderInlined();
    const rr = r as Record<string, unknown>;
    // POS / dine-in invoices store only customerId + name — resolve the full
    // Customer so the print shows address / GSTIN / phone, not just a name.
    let cust: Record<string, unknown> = {};
    const custId = rr.customerId;
    if (custId != null && String(custId) !== '') {
      try {
        const cs = await apiClient.get('/crm/Customer');
        const all = (Array.isArray(cs.data) ? cs.data : (cs.data?.items ?? cs.data?.data?.items ?? cs.data?.data ?? [])) as Array<Record<string, unknown>>;
        cust = all.find((c) => String(c.id) === String(custId) || String(c.code) === String(custId)) ?? {};
      } catch { /* keep row fields */ }
    }
    const gstin = String(r.customerGstin ?? cust.gstin ?? '');
    const custCode = gstin.slice(0, 2);
    const tenantCode = String(tenantH.gstin ?? '').slice(0, 2);
    const interstate = !!custCode && !!tenantCode && custCode !== tenantCode;
    const taxAmount = Number(rr.taxAmount ?? 0);
    const taxable = Number(rr.subTotal ?? rr.taxableAmount ?? 0)
      || lines.reduce((s, l) => s + Number((l as Record<string, unknown>).quantity ?? 0) * Number((l as Record<string, unknown>).unitPrice ?? 0), 0);
    const hsnMap = new Map<string, HsnSummaryRow>();
    for (const l of lines) {
      const lr = l as Record<string, unknown>;
      const hsn = String(lr.hsnCode ?? '') || '—';
      const lTaxable = Number(lr.taxableAmount ?? (Number(lr.quantity ?? 0) * Number(lr.unitPrice ?? 0)));
      const lTax = Number(lr.taxAmount ?? 0);
      const rate = Number(lr.taxRate ?? 0);
      const cur = hsnMap.get(hsn) ?? { hsn, taxable: 0, cgstRate: 0, cgst: 0, sgstRate: 0, sgst: 0, igstRate: 0, igst: 0 };
      cur.taxable += lTaxable;
      if (interstate) { cur.igstRate = rate; cur.igst += lTax; }
      else { cur.cgstRate = rate / 2; cur.cgst += lTax / 2; cur.sgstRate = rate / 2; cur.sgst += lTax / 2; }
      hsnMap.set(hsn, cur);
    }
    const billTo = {
      label: 'Details Of Receiver (Billed To)',
      name: String(r.customerName ?? cust.name ?? ''),
      address: (r.customerAddress ?? cust.billingAddress ?? cust.address) as string | undefined,
      city: (r.customerCity ?? cust.city) as string | undefined,
      state: (r.customerState ?? cust.state) as string | undefined,
      pincode: (r.customerPincode ?? cust.pinCode ?? cust.pincode) as string | undefined,
      gstin,
      phone: (r.customerPhone ?? cust.phone) as string | undefined,
    };
    const payload: InvoicePrintInput = {
      docType: 'TAX INVOICE',
      number: String(r.invoiceNumber ?? id),
      date: String(r.invoiceDate ?? new Date().toISOString()),
      dueDate: r.dueDate,
      customerType: gstin ? 'B2B' : 'B2C',
      tenant: tenantH,
      panNumber: gstin ? gstin.slice(2, 12) : undefined,
      parties: [billTo, { ...billTo, label: 'Details Of Consignee (Shipped To)' }],
      lines: lines.map((l) => {
        const lr = l as Record<string, unknown>;
        return {
          description: String(lr.productName ?? lr.description ?? '—'),
          hsnCode: lr.hsnCode ? String(lr.hsnCode) : undefined,
          qty: Number(lr.quantity ?? lr.qty ?? 0),
          unit: lr.unitOfMeasure ? String(lr.unitOfMeasure) : undefined,
          rate: Number(lr.unitPrice ?? 0),
          taxRate: Number(lr.taxRate ?? 0),
          taxable: Number(lr.taxableAmount ?? 0),
          total: Number(lr.lineTotal ?? 0),
        };
      }),
      subTotal: taxable, totalDiscount: 0, taxableAmount: taxable,
      cgstAmount: interstate ? 0 : taxAmount / 2,
      sgstAmount: interstate ? 0 : taxAmount / 2,
      igstAmount: interstate ? taxAmount : 0,
      roundOff: 0,
      totalAmount: Number(rr.grandTotal ?? r.totalAmount ?? 0),
      hsnSummary: [...hsnMap.values()],
      bank: (b.bankName || b.bankAccountNo) ? { name: b.bankName, accountName: b.bankAccountName, accountNo: b.bankAccountNo, ifsc: b.bankIfsc, branch: b.bankBranch } : undefined,
      signatureUrl: b.signatureUrl,
      qrUrl: printQrUrl(b.qrUrl, ['TAX INVOICE', String(r.invoiceNumber ?? id), billTo.name, 'Amt:' + Number(rr.grandTotal ?? r.totalAmount ?? 0), tenantH.gstin]),
      // SalesInvoice has no couponCode column — recover the applied coupon from
      // the notes envelope the composer wrote ("Coupon: CODE (−₹X)").
      ...(() => {
        const m = /Coupon:\s*([^()|]+?)\s*\(.*?([\d,.]+)\)/.exec(String(rr.notes ?? ''));
        return m ? { couponCode: m[1]!.trim(), couponDiscount: Number(m[2]!.replace(/,/g, '')) || 0 } : {};
      })(),
    };
    printInvoiceByFormat(getAssignedFormat('salesInvoice'), payload);
  }

  async function handleShare(r: InvoiceRow) {
    const total = Number((r as Record<string, unknown>).grandTotal ?? r.totalAmount ?? 0);
    const text = `Invoice ${r.invoiceNumber ?? ''}\nBill to: ${r.customerName ?? '—'}\nTotal: ₹ ${inr(total)}`;
    try {
      const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
      if (typeof nav.share === 'function') await nav.share({ title: `Invoice ${r.invoiceNumber ?? ''}`, text });
      else { await navigator.clipboard.writeText(text); }
    } catch { /* dismissed */ }
  }

  async function handleDc(r: InvoiceRow) {
    const id = String(r.invoiceId ?? r.id ?? '');
    if (!id) return;
    setPrintingDc(id);
    const tenantH = await getTenantHeaderInlined();
    try {
      let lines: InvoiceLineRow[] = [];
      try {
        // The generic CRUD endpoint ignores a `?invoiceId=` query, so fetch
        // the line table and filter by the real FK (`salesInvoiceId`)
        // client-side — otherwise this returned every invoice's lines.
        const ls = await apiClient.get('/sales/SalesInvoiceLine');
        const d = ls.data;
        let all: InvoiceLineRow[] = [];
        if (Array.isArray(d)) all = d as InvoiceLineRow[];
        else if (d && typeof d === 'object') {
          const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
          if (Array.isArray(obj.items)) all = obj.items as InvoiceLineRow[];
          else if (Array.isArray(obj.data)) all = obj.data as InvoiceLineRow[];
          else if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
            all = (obj.data as { items: unknown[] }).items as InvoiceLineRow[];
          }
        }
        lines = all.filter((l) => String((l as { salesInvoiceId?: unknown }).salesInvoiceId ?? '') === String(id));
      } catch {
        lines = [];
      }
      printDeliveryChallan({
        number: `DC-${String(r.invoiceNumber ?? id)}`,
        date: String(r.invoiceDate ?? new Date().toISOString()),
        vehicleNumber: r.vehicleNumber,
        driver: r.driver,
        purpose: 'Goods supplied against tax invoice',
        tenant: tenantH,
        parties: [
          {
            label: 'Consignor',
            name: tenantH.name,
            address: tenantH.address,
            city: tenantH.city,
            state: tenantH.state,
            pincode: tenantH.pincode,
            gstin: tenantH.gstin,
            phone: tenantH.phone,
            email: tenantH.email,
          },
          {
            label: 'Consignee',
            name: String(r.customerName ?? ''),
            address: r.customerAddress,
            city: r.customerCity,
            state: r.customerState,
            pincode: r.customerPincode,
            gstin: r.customerGstin,
            phone: r.customerPhone,
          },
        ],
        lines: lines.map((l) => ({
          description: String(l.description ?? l.productName ?? '—'),
          hsnCode: l.hsnCode,
          qty: Number(l.quantity ?? l.qty ?? 0),
          unit: l.unit,
        })),
        notes: `Issued against invoice ${r.invoiceNumber ?? id}`,
      });
    } finally {
      setPrintingDc(null);
    }
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">{meta.title}</h1>
          <p className="text-xs text-content-muted">
            {meta.subtitle} · live /api/sales/SalesInvoice
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Reload
        </Button>
        {meta.canUpload && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/panel/transactions/ingest')}
            title="Drop a PDF tax invoice — auto-create items + post in one step"
          >
            <Upload className="h-3.5 w-3.5" /> Upload PDF
          </Button>
        )}
        <Button size="sm" onClick={() => navigate(meta.newTo)}>
          <Plus className="h-3.5 w-3.5" /> {meta.newLabel}
        </Button>
      </div>

      {/* Dine-in: let staff bill OPEN restaurant orders straight from this page
          (not only from the Floor). Self-renders nothing when none are open. */}
      {source === 'Dine-in' && <DineInOpenOrders onBilled={load} />}

      {/* Module tabs — each links to a SEPARATE per-channel page (Sales /
          POS / Dine-in). The count is across all loaded rows so every page
          shows the same channel totals. */}
      <div className="flex flex-wrap items-center gap-1.5">
        {MODULE_TABS.map((t) => {
          const active = source === t.source;
          const count = rows.filter((r) => sourceOf(r) === t.source).length;
          return (
            <button
              key={t.source}
              type="button"
              onClick={() => navigate(t.to)}
              className={`rounded-full border px-3 py-1 text-xs ${
                active
                  ? 'border-primary bg-primary text-white'
                  : 'border-border bg-surface-card text-content-secondary hover:border-primary'
              }`}
            >
              {t.label}
              <span className="ml-1 opacity-70">{count}</span>
            </button>
          );
        })}
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Invoices"
          value={kpis.total.toLocaleString()}
          subtitle="All statuses"
          icon={<FileText className="h-5 w-5 text-info" />}
        />
        <KpiCard
          title="Revenue"
          value={`₹ ${inr(kpis.totalRevenue)}`}
          subtitle="Sum of all invoices"
          icon={<TrendingUp className="h-5 w-5 text-success" />}
        />
        <KpiCard
          title="Outstanding"
          value={`₹ ${inr(kpis.outstanding)}`}
          subtitle="Unpaid balance"
          icon={<IndianRupee className="h-5 w-5 text-danger" />}
        />
        <KpiCard
          title="Overdue"
          value={kpis.overdue.toLocaleString()}
          subtitle="Past due date"
          icon={<AlertTriangle className={`h-5 w-5 ${kpis.overdue ? 'text-danger' : 'text-success'}`} />}
        />
      </div>

      {loading && rows.length === 0 ? (
        <Card>
          <div className="flex items-center gap-2 p-6 text-sm text-content-muted">
            <Spinner /> Loading invoices…
          </div>
        </Card>
      ) : rows.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">
            No invoices yet. Click <em>{meta.newLabel}</em> to create your first one.
          </div>
        </Card>
      ) : visibleRows.length === 0 ? (
        <Card>
          <div className="p-6 text-sm text-content-muted">
            No {meta.title.toLowerCase()} yet. Use <em>{meta.newLabel}</em> to add one — or switch channel above.
          </div>
        </Card>
      ) : (
        <DataGrid<InvoiceRow>
          data={visibleRows}
          columns={columns}
          rowId={(rows[0]?.invoiceId != null ? 'invoiceId' : 'id') as keyof InvoiceRow & string}
          defaultSort={{ columnAccessor: 'invoiceDate', direction: 'desc' }}
        />
      )}
    </div>
  );
}

/**
 * Invoice source/type derived from the code prefix. The backend
 * SalesInvoice table has no `type`/`source` column (see G65), so we
 * encode the channel in the code prefix:
 *   POS- → POS · DIN- → Dine-in · everything else → Self (manual invoice)
 */
function SourcePill({ label }: { label: 'Self' | 'POS' | 'Dine-in' }) {
  let cls = 'border-info/40 bg-info/10 text-info';
  if (label === 'POS') cls = 'border-success/40 bg-success/10 text-success';
  else if (label === 'Dine-in') cls = 'border-warning/40 bg-warning/10 text-warning';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{label}</span>
  );
}

function StatusPill({ v }: { v: string }) {
  const s = v.toLowerCase();
  let cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  if (s === 'paid') cls = 'border-success/40 bg-success/10 text-success';
  else if (s === 'posted') cls = 'border-info/40 bg-info/10 text-info';
  else if (s === 'overdue') cls = 'border-danger/40 bg-danger/10 text-danger';
  else if (s === 'draft') cls = 'border-warning/40 bg-warning/10 text-warning';
  else if (s === 'cancelled') cls = 'border-content-muted/40 bg-content-muted/10 text-content-muted';
  return (
    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] capitalize ${cls}`}>
      {v || '—'}
    </span>
  );
}

function fmtDate(v: unknown): string {
  if (typeof v !== 'string' || !v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v.slice(0, 10);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
